<template>
  <div id="transfer-v2" class="">
    <div
      v-if="anyBootstrapError()"
      class="mb-4 arcadi-alert--error flex flex-wrap items-center justify-between gap-2"
      role="alert"
    >
      <span>{{ $t("bootstrap_recovery.error") }}</span>
      <button type="button" class="underline font-medium" @click="retryFailedBootstraps()">
        {{ $t("bootstrap_recovery.retry") }}
      </button>
    </div>
    <div
      v-if="!loading && !anyBootstrapError() && Object.values(sourceCountries || {}).length === 0"
      class="mb-4 arcadi-alert--error"
    >
      No hay tasas de cambio válidas en este momento, inténtelo de nuevo más
      adelante!
    </div>
    <div class="flex justify-between items-center mt-5 mb-2">
      <span class="font-color-primary ml-4">{{
      $t("simulator_page.you_pay")
    }}</span>
      <!-- ARCADI-EA: deshabilitado hasta que el bootstrap de países esté OK —
           antes, un click durante la ventana de carga reventaba con
           selectedBalance undefined. Si la carga falla, la franja de error
           del mixin (con Reintentar) explica por qué el botón no responde. -->
      <button type="button" @click="handleUseMaxBalance" class="button-arcadi-simulator-all"
        :disabled="bootstrapState('countries') !== 'ok'">
        <span v-if="selectedBalance && -selectedBalance.balance > 0">
          {{ $t("simulator_page.use_all") }}
        </span>
        <span v-else>
          {{ $t("simulator_page.deposit") }}
        </span>
      </button>
    </div>
    <div class="flex flex-col transfer-amount-container-body" :class="[{ 'mb-3': !errorAmount() }]">
      <div class="flex money-currency-row">
        <v-select v-model="selectedSourceCountry" :options="sourceCountriesOrder.map(countryId => sourceCountries[countryId])
      " appendToBody :calculate-position="positionMoneyCurrencyMenu" label="name" class="flex items-center money-currency-select" :clearable="false" :searchable="false"
          :noDrop="Object.keys(sourceCountries || {}).length < 2"
          :tabindex="Object.keys(sourceCountries || {}).length < 2 ? -1 : 0"
          :class="{ 'money-currency-select--static': Object.keys(sourceCountries || {}).length < 2 }">
          <template v-slot:[slotName]="country" v-for="slotName in ['option', 'selected-option']">
            <span class="overflow-visible whitespace-nowrap" :class="{ 'money-currency-option': slotName === 'option' }" :key="slotName">
              <img :src="country.urlPath" class="inline align-middle flag-style"
                style="min-width: 34px; max-width: 34px" />
              <span v-if="slotName === 'option'" class="money-currency-option__label">{{ country.currency }}</span>
            </span>
          </template>
          <template #no-options> No hay tasas disponibles </template>
        </v-select>
        <div class="w-full money-input-presentation__field">
          <input type="tel" id="sourceAmount_input" inputmode="decimal" :rawvalue="sourceAmount" autocomplete="off"
            @input="handleSourceAmountInput" v-numeric="sourceAmountMask" data-money-input-display
            :data-money-input-locale="$i18n.locale" :data-money-input-currency="selectedSourceCurrency"
            :data-money-input-precision="selectedSourceCountry?.currency === 'CLP' ? 0 : 2"
            class="w-full arcadi-input shadow-none border-gray-custom rounded-right bg-transparent"
            style="height: 77px; font-weight: 500; font-size: 24px" :disabled="amountDisabled && !exchangeValid" />
        </div>
      </div>
    </div>
    <span v-if="errorAmount() && !loading" class="ml-4 mb-2 text-sm text-red-400">{{
      errorAmount()
    }}</span>
    <div v-if="selectedSourceCurrency" class="flex w-full mb-4 text-sm font-color-primary">
      <span class="font-semibold ml-4">{{ $t("simulator_page.actual_money") }}:</span>
      <format-amount :amount="selectedBalance && Number(selectedBalance.balance)
      ? -selectedBalance.balance
      : 0
      " :currency="selectedSourceCurrency" :maximumFractionDigits="selectedSourceCountry?.currency === 'CLP' ? 0 : 2
      " />
    </div>
    <template v-if="showDetails">
      <div v-if="showRateDependentFields" class="flex mb-2">
        <span class="font-color-primary ml-4">{{
      $t("simulator_page.recipient_gets")
    }}</span>
      </div>
      <div v-if="showRateDependentFields" class="flex mb-4 transfer-amount-container-body transfer-amount-container-body-green money-currency-row">
        <v-select id="destinationCountry" v-model="selectedDestinationCountry" appendToBody :calculate-position="positionMoneyCurrencyMenu" label="name"
          class="flex items-center money-currency-select" style="min-width: 80px;" :clearable="false" :searchable="false" :disabled="true">
          <template v-slot:[slotName]="country" v-for="slotName in ['option', 'selected-option']">
            <span class="overflow-visible whitespace-nowrap" :class="{ 'money-currency-option': slotName === 'option' }" :key="slotName">
              <img :src="country.urlPath" class="inline align-middle flag-style"
                style="min-width: 34px; max-width: 34px" />
              <span v-if="slotName === 'option'" class="money-currency-option__label">{{ country.currency }}</span>
            </span>
          </template>
          <template v-slot:no-options> No hay tasas disponibles </template>
        </v-select>
        <div class="flex item-center w-full money-currency-amount">
          <format-amount variant="primary" :amount="destinationAmount" :currency="selectedExchangeRateCurrency"
            :maximumFractionDigits="selectedExchangeRateCurrency === 'CLP' ? 0 : 2"
            :minimumFractionDigits="selectedExchangeRateCurrency === 'CLP' ? 0 : 2" :hideZeroDecimals="true" />
        </div>
        <div v-show="isPeru" id="destination_amount" class="flex items-center justify-end pr-8">
          <v-select v-model="selectedDestinationCurrency" appendToBody :calculate-position="positionMoneyCurrencyMenu" :clearable="false" :searchable="false"
            :options="Object.keys(exchangeRateList || {})" class="w-16 currency-selector money-currency-select money-currency-select--compact"
            v-bind:class="{ 'hide-when-not-peru': !isPeru }">
            <template #selected-option>
              <span></span>
            </template>
            <template #option="option">
              <span class="money-currency-option money-currency-option--code">{{ (option.label || "").toUpperCase() }}</span>
            </template>
          </v-select>
        </div>
      </div>
      <div v-show="isPeru && (selectedDestinationCurrency || '').toUpperCase() === 'USD'" class="warning-message text-center">
        {{ $t("simulator_page.usd_peru_alert") }}
      </div>
      <!-- 3 estados (mismo patrón que Comprar): cargando (nada — no flashear
           un aviso falso), sin tasa utilizable (aviso honesto standalone,
           nunca un "1 = " colgante ni "recibirás 0"), o el box de tasa real. -->
      <div v-if="ratesLoaded && !hasExchangeRate" id="no-exchange-rate-warning" class="card-preferences card-preferences__notice no-rate-notice" role="status">
        <span class="material-icons-sharp" aria-hidden="true">hourglass_empty</span>
        <div class="no-rate-notice__text">
          <span class="no-rate-notice__title">
            {{ hasRatesForSelectedCurrency
              ? $t("simulator_page.no_exchange_rate_for_amount_title")
              : $t("simulator_page.no_exchange_rate_title") }}
          </span>
          <span class="no-rate-notice__body">
            {{ hasRatesForSelectedCurrency
              ? $t("simulator_page.no_exchange_rate_for_amount")
              : $t("simulator_page.no_exchange_rate") }}
          </span>
        </div>
      </div>
      <div v-else-if="ratesLoaded" class="p-2 p-6 mb-4 text-sm bg-white rounded-lg lg:text-base exchange-container">
          <div id="exchange-rate" class="flex justify-between mb-4">
            <span class="font-normal text-left">
              {{ $t("simulator_page.exchange_rate") }}:
            </span>
            <span v-if="Number(selectedExchangeRate?.rate) > 1" class="flex text-right whitespace-nowrap">
              {{
      AutoNumeric.format(1, exchangeRateMask(selectedSourceCurrency))
    }}
              =
              <format-amount :amount="selectedExchangeRateFeeles" :currency="selectedExchangeRateCurrency"
                :maximumFractionDigits="2" :minimumFractionDigits="2" />
            </span>
            <span v-else class="flex text-right whitespace-nowrap">
              {{
      AutoNumeric.format(
        1,
        exchangeRateMask(selectedExchangeRateCurrency)
      )
    }}
              =
              <format-amount :amount="1 / selectedExchangeRateFeeles" :currency="selectedSourceCurrency"
                :maximumFractionDigits="2" :minimumFractionDigits="2" />
            </span>
          </div>
          <hr class="w-full text-gray-10% mb-2" />
          <div id="total-commision" class="relative flex justify-between my-4">
            <span class="font-normal text-left">
              {{ $t("simulator_page.total_commission") }}:
            </span>
            <span class="text-right whitespace-nowrap">
              <format-amount :amount="commission" :currency="selectedSourceCurrency" :maximumFractionDigits="selectedSourceCountry?.currency === 'CLP' ? 0 : 2
      " :minimumFractionDigits="selectedSourceCountry?.currency === 'CLP' ? 0 : 2
      " />
            </span>
            <span style="position: absolute; right: -24px; top: 5px" class="tooltip">
              <span style="margin-left: -100px; " class="tooltiptext">
                {{ tooltipMessage }}
              </span>
              <img class="tooltip-image tooltip-image-primary px-2" :src="images.infoIcon" />
            </span>
          </div>
          <hr class="w-full text-gray-10% mb-2" />
          <div id="final-amount" class="flex justify-between">
            <span class="font-normal text-left">
              {{ $t("simulator_page.mount_to_send") }}:
            </span>
            <span class="text-right whitespace-nowrap">
              <format-amount :amount="totalToSend" :currency="selectedSourceCurrency"
                :maximumFractionDigits="selectedSourceCountry?.currency === 'CLP' ? 0 : 2"
                :minimumFractionDigits="selectedSourceCountry?.currency === 'CLP' ? 0 : 2" />
            </span>
          </div>
      </div>
      <div v-if="showRateDependentFields" id="arrive-date" class="flex mb-4 money-will-arrive-container" style="padding-left: 10px">
        <span class="text-sm lg:text-base font-normal">
          {{ $t("simulator_page.money_will_receive") }}:
          <b>
            {{
            selectedDestinationCountry
            ? selectedDestinationCountry?.eta
            : "N/A"
            }}
          </b>
        </span>
      </div>
    </template>
  </div>
</template>

<style scoped>
#transfer-v2 .vs__dropdown-toggle,
#transfer-v2 .vue__arcadi-input {
  height: 100%;
  background-color: transparent m !important;
  -webkit-border-radius: 0.6rem 0 0 0.6rem;
  -moz-border-radius: 0.6rem 0 0 0.6rem;
  border-radius: 0.6rem 0 0 0.6rem;
}

#transfer-v2>>>.vs__dropdown-toggle {
  border: none;
  border-radius: 0;
  background: transparent;
}

.simulator-vue {
  max-width: 545px;
  border-radius: 30px;
  font-family: "Poppins", sans-serif;
  color: #262626;
}

.arcadi-input:disabled {
  background: transparent;
}

.transfer-amount-container-body {
  border-radius: 27px;
  border-left: 6px solid #611de9;
  background: #f9f9f9;
  height: 77px;
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
}

.transfer-amount-container-body-green {
  border-left: 6px solid #ccff00;
}

.simulator-vue span {
  font-family: "Poppins", sans-serif;
  color: black;
}

.simulator-vue input {
  font-family: "Poppins", sans-serif;
  color: black;
}

@media screen and (max-width: 992px) {
  .simulator-vue {
    max-width: 100%;
  }
}

@media screen and (max-width: 720px) {
  .simulator-vue {
    border-radius: 0px 0px 35px 35px;
  }
}

.currency-selector.v-select,
.currency-selector.v-select .vs__selected-options {
  min-width: 0px;
}

.currency-selector.v-select .vs__dropdown-toggle {
  border-left: 0;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.vs__dropdown-toggle {
  border: none;
  border-radius: 0;
}

.v-select {
  min-width: 65px;
}

.tooltip-image-primary {
  filter: brightness(0) saturate(100%)
}

.vs__selected-options {
  min-width: 35px;
  margin-left: 10px;
  margin-right: 10px;
}

.vs--single.vs--open .vs__selected {
  position: relative;
}

.vs--disabled div.vs__dropdown-toggle {
  background-color: transparent !important;
  cursor: not-allowed;
}

.vs__dropdown-menu {
  min-width: 0px;
  width: auto;
}

.vs__dropdown-menu li {
  padding: 4px 0 4px 10px;
}

.vs__dropdown-menu span {
  font-family: "Poppins", sans-serif;
}

.vs__open-indicator {
  transform: scale(0.8);
}

.vs__search {
  position: absolute;
}

.vs--unsearchable .vs__search {
  opacity: 0;
}

.exchange-container {
  border-radius: 27px;
  background: #f9f9f9;
}

.warning-message {
  background-color: #f19191;
  margin-bottom: 1rem;
  border-radius: 0.6rem;
  border-bottom: 2px solid red;
}

.float {
  position: fixed;
  width: 60px;
  height: 60px;
  bottom: 40px;
  right: 40px;
  background-color: #25d366;
  color: #fff;
  border-radius: 50px;
  text-align: center;
  font-size: 30px;
  box-shadow: 2px 2px 3px #999;
  z-index: 100;
}

.my-float {
  margin-top: 16px;
}

#transfer-v2>>>#destinationCountry .vs__actions {
  visibility: hidden;
}

.rounded-right {
  -webkit-border-radius: 0 0.6rem 0.6rem 0;
  -moz-border-radius: 0 0.6rem 0.6rem 0;
  border-radius: 0 0.6rem 0.6rem 0;
}
</style>

<script>
import { positionMoneyCurrencyMenu } from "../../../money-currency-menu";
import { normalize } from "normalizr";
import schema from "../../../schema";
import AutoNumeric from "autonumeric";
import formatAmount from "../amount/format-amount.vue";

import infoIcon from "../../../../assets/images/info-icon.svg";

import SimulatorApi from "../../../api/simulator";
import BootstrapRecovery from "../../../mixins/bootstrap-recovery";
import { getLastValidExchangeRateV2 } from "../../../utils/exchange_rates";

export default {
  mixins: [BootstrapRecovery],
  beforeMount() {
    this.getSimulatorData();
    this.getTransferLimits();
  },
  components: {
    formatAmount
  },
  props: {
    sourceCountriesCode: {
      type: Array,
      default: []
    },
    goTo: {
      type: String,
      default: ""
    },
    availableBalance: {
      type: Number,
      required: true
    },
    balanceAccounts: {
      type: Array,
      required: true
    },
    destinationCurrencies: {
      type: Object
    },
    bank: {
      type: String,
      required: true
    }
  },
  computed: {
    currencies() {
      // Disponible se contabiliza con signo negativo. USDC exige saldo propio,
      // además de estar en los países/corredores recibidos del bootstrap.
      return this.balanceAccounts
        .filter(account => account.currency !== "USDC" ||
          (Number.isFinite(Number(account.balance_cents)) && Number(account.balance_cents) < 0))
        .map(account => account.currency);
    },
    isCrypto() {
      const crypto = ["USDT", "USDC"];
      return crypto.includes(this.selectedDestinationCountry?.currency);
    },
    tooltipMessage() {
      return this.isCrypto
        ? this.$t("simulator_page.tooltips.blockchain")
        : this.$t("simulator_page.tooltips.normal");
    },
    selectedDestinationCountry() {
      if (Object.values(this.destinationCurrencies || {}).length > 0) {
        const currency = Object.keys(this.destinationCurrencies || {})[0];
        const currencyId = this.destinationCurrencies[currency][0].country_id;
        const destination = this.destinationCountries[currencyId];
        return destination;
      }
      return {};
    },
    selectedDestinationCountries() {
      if (Object.values(this.selectedSourceCountry || {}).length > 0 && this.selectedSourceCountry.destinationCountries) {
        return Object.values(this.selectedSourceCountry.destinationCountries || {})
          .length > 0
          ? this.selectedSourceCountry.destinationCountries.map(
            id => this.destinationCountries[id]
          )
          : [];
      }

      return [];
    },
    sourceAmountMask() {
      return {
        allowDecimalPadding: "floats",
        modifyValueOnWheel: false,
        decimalPlaces: this.selectedSourceCountry?.currency === "CLP" ? 0 : 2,
        decimalPlacesRawValue: null,
        caretPositionOnFocus: "end",
        selectOnFocus: false,
        selectNumberOnly: true,
        decimalCharacter: this.$i18n.locale === "en" ? "." : ",",
        digitGroupSeparator: this.$i18n.locale === "en" ? "," : ".",
        currencySymbol: ` ${this.selectedSourceCurrency || ""}`,
        currencySymbolPlacement: "s",
        emptyInputBehavior: "always"
      };
    },
    destinationAmountMask() {
      return {
        allowDecimalPadding: "floats",
        decimalPlaces: 2,
        decimalPlacesRawValue: null,
        decimalCharacter: this.$i18n.locale === "en" ? "." : ",",
        digitGroupSeparator: this.$i18n.locale === "en" ? "," : ".",
        currencySymbol: ` ${this.selectedExchangeRateCurrency || ""}`,
        currencySymbolPlacement: "s",
        emptyInputBehavior: "always",
        readOnly: true
      };
    },
    selectedSourceCurrency() {
      return this.selectedSourceCountry?.currency;
    },
    selectedExchangeRateCurrency() {
      return (this.selectedDestinationCurrency || "").toUpperCase();
    },
    selectedExchangeRateFeeles() {
      return (
        Number(this.selectedExchangeRate?.rate) / (1 - this.percentageCommission)
      );
    },
    selectedTransferLimit() {
      return (
        this.transferLimits.find(transferLimit => {
          return (
            transferLimit.sourceCountryId == this.selectedSourceCountry?.id &&
            transferLimit.destinationCountryId ==
            this.selectedDestinationCountry?.id
          );
        }) || {}
      );
    },
    percentageCommission() {
      return this.selectedExchangeRate?.fee;
    },
    destinationAmount() {
      if (Object.keys(this.selectedExchangeRate || {}).length === 0) {
        return 0;
      }

      return (
        this.sourceAmount * Number(this.selectedExchangeRate?.rate) -
        this.fixedFee
      );
    },
    fixedFee() {
      const specialFee = {
        TRC20: 2.5,
        ERC20: 7.5
      };
      if (
        (this.selectedSourceCurrency || "").toUpperCase() == "USDT" &&
        (this.selectedDestinationCurrency || "").toUpperCase() == "USDT" &&
        specialFee[this.bank]
      ) {
        return specialFee[this.bank];
      }
      return 0;
    },
    commission() {
      return this.destinationAmount > 0
        ? this.sourceAmount -
        this.sourceAmount * (1 - this.percentageCommission) +
        this.fixedFee
        : 0;
    },
    totalToSend() {
      return this.destinationAmount > 0
        ? this.sourceAmount * (1 - this.percentageCommission)
        : 0;
    },
    selectedBalance() {
      return this.balanceAccounts.find((account) => {
        return account.currency === this.selectedSourceCurrency
      });
    },
    showDetails() {
      // La segunda condición fuerza la región visible SOLO en corredor-vacío
      // (aviso inmediato sin esperar un monto). Cuando SÍ hay tramos pero aún
      // no se tipea monto, la región queda oculta como siempre — sin esto, un
      // corredor cuyos tramos tienen mínimo > 0 mostraba "Sin tasa para este
      // monto" antes de que el usuario escribiera nada.
      return (
        (!this.errorAmount() &&
          this.sourceAmount !== null &&
          parseFloat(this.sourceAmount) > 0) ||
        (this.ratesLoaded && !this.hasExchangeRate && !this.hasRatesForSelectedCurrency)
      );
    },
    // Decisión de producto (2026-07-20): "Destinatario recibe" se oculta en
    // TODOS los estados sin tasa, incluido fuera-de-tramo en Perú — el selector
    // USD/PEN deja de ser alcanzable ahí a propósito (el remedio es ajustar el
    // monto; supersede la recomendación de la revisión adversarial).
    hasExchangeRate() {
      // setExchangeRate deja {} cuando no hay tasa utilizable para el monto
      // (getLastValidExchangeRateV2 filtra por tramos [min, max]). Una fila
      // degenerada (rate 0 / fee >= 1) daría un feeles no-finito o <= 0:
      // también cuenta como sin tasa. Gatea el aviso, los campos dependientes
      // y el botón Continuar del ERB.
      return (
        Boolean(this.selectedExchangeRate?.id) &&
        Number.isFinite(this.selectedExchangeRateFeeles) &&
        this.selectedExchangeRateFeeles > 0
      );
    },
    hasRatesForSelectedCurrency() {
      // Distingue "corredor sin tasas" de "el monto quedó fuera de todos los
      // tramos de las tasas existentes": en el segundo caso el remedio es
      // ajustar el monto, no "intenta más tarde".
      return (this.exchangeRateList?.[this.selectedDestinationCurrency] || []).length > 0;
    },
    showRateDependentFields() {
      // Mientras carga se mantiene el layout normal; una vez cargado sin tasa
      // utilizable, "recibirás", la ETA y el CTA se ocultan — el aviso
      // no-rate-notice ocupa su lugar.
      return !this.ratesLoaded || this.hasExchangeRate;
    }
  },
  data() {
    return {
      selectedDestinationCurrency: "",
      sourceCountriesOrder: [],
      sourceCountries: {},
      destinationCountries: {},
      loading: true,
      transferLimits: [],
      selectedSourceCountry: {},
      exchangeRateList: {},
      sourceAmount: 0,
      selectedExchangeRate: {},
      AutoNumeric,
      amountDisabled: false,
      exchangeValid: true,
      // Evita flashear el aviso "sin tasa" durante la carga inicial y permite
      // distinguir "cargando" de "el corredor no tiene tasas".
      ratesLoaded: false,
      images: {
        infoIcon: infoIcon
      },
      isPeru: false
    };
  },
  methods: {
    positionMoneyCurrencyMenu,
    exchangeRateMask(currencySymbol) {
      return {
        allowDecimalPadding: "floats",
        decimalPlaces: 2,
        roundingMethod: "S",
        decimalPlacesRawValue: null,
        decimalCharacter: this.$i18n.locale === "en" ? "." : ",",
        digitGroupSeparator: this.$i18n.locale === "en" ? "," : ".",
        currencySymbolPlacement: "s",
        emptyInputBehavior: "always",
        readOnly: true,
        currencySymbol: ` ${currencySymbol}`
      };
    },
    getSimulatorData() {
      // ARCADI-E9 (familia): el `data.filter is not a function` de esta vista
      // era la respuesta fantasma tratada como lista. Recuperación acotada +
      // validate de forma (mixin bootstrap-recovery).
      this.loadWithRecovery("countries", {
        request: () => SimulatorApi.getCountries(),
        validate: data => Array.isArray(data),
        assign: ({ data }) => {
          data = data.filter(country =>
            this.currencies.includes(country.currency)
          );
          const normalizedData = normalize(data, [schema.sourceCountry]);
          this.sourceCountriesOrder = normalizedData.result || [];
          this.sourceCountries = normalizedData.entities.sourceCountries || {};
          this.destinationCountries =
            normalizedData.entities.destinationCountries || {};
          this.loading = false;
        }
      });
    },
    getTransferLimits() {
      this.loadWithRecovery("transferLimits", {
        request: () => SimulatorApi.getTransferLimits(),
        validate: data => Array.isArray(data), // ARCADI-F4
        assign: ({ data }) => {
          this.transferLimits = data;
        }
      });
    },
    getExchangeRateList() {
      // Sin destino con id no hay nada que consultar (corredor sin ninguna
      // CurrentExchangeRate — con @exchange_rates == {} desde el server el
      // destino queda {}): es directamente el estado "sin tasas" cargado.
      // Un guard truthy sobre {} dejaría pasar un fetch con id undefined y
      // ratesLoaded nunca se activaría — aviso mudo para siempre.
      if (!this.selectedSourceCountry?.id || !this.selectedDestinationCountry?.id) {
        this.markRatesUnavailable();
        return;
      }

      // Secuenciación: un fetch viejo que falla LENTO no debe pisar (vía
      // markRatesUnavailable) las tasas que un fetch más nuevo ya cargó.
      this._rateReqSeq = (this._rateReqSeq || 0) + 1;
      const seq = this._rateReqSeq;

      SimulatorApi.getExchangeRates(
        this.selectedSourceCountry.id,
        this.selectedDestinationCountry.id
      ).then(({ data }) => {
        if (seq !== this._rateReqSeq) return;
        if (Object.keys(data).length === 0) {
          this.markRatesUnavailable();
          return;
        }
        this.amountDisabled = false;
        this.exchangeValid = true;
        this.exchangeRateList = data;
        this.selectedDestinationCurrency = Object.keys(data)[0];
        this.setExchangeRate();
        this.ratesLoaded = true;
      }).catch(() => {
        if (seq !== this._rateReqSeq) return;
        // Fetch caído: mejor el aviso honesto (y CTA bloqueado) que un
        // contenedor mudo sin explicación.
        this.markRatesUnavailable();
      });
    },
    markRatesUnavailable() {
      this.amountDisabled = true;
      this.exchangeValid = false;
      this.exchangeRateList = {};
      this.selectedExchangeRate = {};
      this.ratesLoaded = true;
    },
    restartAmounts() {
      this.sourceAmount = null;
    },
    syncContinueButton() {
      const continueButton = document.querySelector("#arcadiAmountContinue");
      if (!continueButton) return; // el ERB lo omite en corredor sin tasas

      continueButton.disabled = !(
        this.sourceAmount > 0 && !this.errorAmount() && this.hasExchangeRate
      );
    },
    setExchangeRate() {
      if (Object.keys(this.exchangeRateList || {}).length === 0) {
        // Limpiar la tasa stale del par anterior (mismo patrón que Comprar):
        // el early-return de antes dejaba seleccionada una tasa que ya no
        // corresponde al corredor actual.
        this.selectedExchangeRate = {};
        return;
      }

      this.selectedExchangeRate =
        getLastValidExchangeRateV2(
          this.exchangeRateList[this.selectedDestinationCurrency] || [],
          this.sourceAmount
        ) || {};
    },
    handleSourceAmountInput(event) {
      this.sourceAmount = event.target.autonumeric.rawValue;
      document.getElementById("arcadiSourceAmount").value = this.sourceAmount;
    },
    handleUseMaxBalance(event) {
      if (this.amountDisabled) {
        return false;
      }

      // ARCADI-EA (cinturón del :disabled): en la ventana de bootstrap
      // selectedBalance es undefined — sin saldo definido no hay "todo" que
      // usar, y mandar a /top_up sería mentirle a un usuario que puede tener
      // saldo de sobra. Se ignora el click; NUNCA se inventa un cero.
      if (!this.selectedBalance) {
        return false;
      }

      if (-this.selectedBalance.balance <= 0) {
        window.location.pathname = "/top_up";
      }

      this.sourceAmount = -this.selectedBalance.balance
      document.getElementById("arcadiSourceAmount").value = this.sourceAmount;
    },
    errorAmount() {
      if (!this.selectedBalance) {
        return `${this.$t("restrictions.more_than_balance")}`;
      } else if (
        this.sourceAmount > 0 &&
        this.sourceAmount < this.selectedTransferLimit.minimumAmount
      ) {
        return `${this.$t("restrictions.minimum_ammount")} ${AutoNumeric.format(
          this.selectedTransferLimit.minimumAmount,
          this.sourceAmountMask
        )}`;
      } else if (this.sourceAmount > this.selectedTransferLimit.maximumAmount) {
        return `${this.$t("restrictions.maximum_ammount")} ${AutoNumeric.format(
          this.selectedTransferLimit.maximumAmount,
          this.sourceAmountMask
        )}`;
      } else if (
        this.selectedBalance &&
        this.sourceAmount > -this.selectedBalance.balance
      ) {
        return `${this.$t("restrictions.more_than_balance")}`;
      }

      return false;
    }
  },
  watch: {
    selectedSourceCountry(newValue, oldValue) {
      // Escribir el campo SOLO con una moneda real: si newValue es null (vue-select
      // 4 lo nulea al vaciarse las opciones) o {} (estado inicial), NO se pisa el
      // valor server-seeded con "undefined"/null. Antes: newValue.currency
      // reventaba con null (abortando el watcher) y escribía el string "undefined"
      // con {} — ambos landmines. Los fetch quedan siempre (intención original).
      if (newValue && newValue.currency) {
        document.getElementById("arcadiSourceAmountCurrency").value =
          newValue.currency;
      }
      this.getSimulatorData();
      this.restartAmounts();
      // Volver al estado "cargando" ANTES del fetch: sin esto, la lista de
      // tramos del corredor anterior seguía viva mientras llegaba la nueva y
      // el aviso/botón sacaban conclusiones con tasas stale.
      this.exchangeRateList = {};
      this.setExchangeRate();
      this.ratesLoaded = false;
      this.getExchangeRateList();
    },
    sourceAmount(newValue, oldValue) {
      this.setExchangeRate();
      this.syncContinueButton();
    },
    // El botón vive en el ERB (show_amount.html.erb) y puede NO existir: la
    // vista lo omite cuando el corredor no tiene ninguna tasa. Además de monto
    // y límites, exige una tasa utilizable — sin este gate se podía continuar
    // con selectedExchangeRate vacío (monto fuera de todos los tramos).
    hasExchangeRate() {
      this.syncContinueButton();
    },
    loading(val) {
      if (val === false) {
        this.selectedSourceCountry =
          this.sourceCountries[this.sourceCountriesOrder[0]] || {};
        // Se removieron 3 líneas que calculaban `destination` sin usarlo y
        // reventaban con `destinationCurrencies[undefined][0]` cuando la moneda
        // de origen del usuario no tiene tasas de cambio configuradas
        // (destinationCurrencies vacío). El backend YA maneja ese caso vacío en
        // show_amount.html.erb; el crash del watcher saboteaba ese fallback.
        // Código muerto: `destination` nunca se usaba.
        this.getSimulatorData();
        this.getExchangeRateList();
      }
    },
    selectedDestinationCountry(val) {
      // Guardar el .name: vue-select 4 puede nulear el v-model (isPeru -> false,
      // el default correcto) en vez de reventar el watcher.
      this.isPeru = val?.name == "Perú";
    },
    selectedDestinationCurrency() {
      document.getElementById(
        "transfer_exchange_rate_destination_currency"
      ).value = (this.selectedDestinationCurrency || "").toUpperCase();
      this.setExchangeRate();
    }
  }
};
</script>
